import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Crown, X, SlidersHorizontal, User, ShieldCheck, Map, LayoutGrid } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { CITIES, PROPERTY_TYPES, LISTING_TYPES, AMENITIES } from "@/lib/format";
import { BRAND } from "@/lib/api";

const LOC_KEYS = ["city", "district", "metro", "metroDist"];
const ROOM_KEYS = ["rooms", "bedrooms", "wcSeparate", "bathTub"];
const SURF_KEYS = ["areaMin", "areaMax", "kitchenMin", "kitchenMax", "livingMin", "livingMax"];
const PRICE_KEYS = ["priceMin", "priceMax", "noCommission"];
const OTHER_KEYS = ["parking", "undergroundOnly", "furnished", "pets", "availableFrom", "builtFrom", "builtTo", "publication", "cargoLift", "directOwner", "kidsAllowed"];

const anySet = (filters, keys) => keys.some((k) => filters[k]);

export const FilterBar = ({ filters, setFilter, resetFilters, total, sort, setSort, view, setView }) => {
  const { lang, t } = useLang();
  const [openId, setOpenId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const barRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) setOpenId(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 64);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goldOn = filters.onlyGold !== "0";
  const allKeys = ["dealType", "propertyType", ...LOC_KEYS, ...ROOM_KEYS, ...SURF_KEYS, ...PRICE_KEYS, ...OTHER_KEYS, "amenities", "onlyVerified"];
  const hasAny = allKeys.some((k) => filters[k]);
  const activeCount = allKeys.filter((k) => filters[k]).length;

  const selectedAmenities = filters.amenities ? filters.amenities.split(",").filter(Boolean) : [];
  const toggleAmenity = (key) => {
    const set = new Set(selectedAmenities);
    if (set.has(key)) set.delete(key); else set.add(key);
    setFilter("amenities", Array.from(set).join(","));
  };

  const dealOptions = [{ v: "", l: t("any_short") }, ...Object.entries(LISTING_TYPES).map(([k, val]) => ({ v: k, l: val[lang] }))];
  const ptypeOptions = [{ v: "", l: t("anyType") }, ...Object.entries(PROPERTY_TYPES).map(([k, val]) => ({ v: k, l: val[lang] }))];
  const dealLabel = filters.dealType ? LISTING_TYPES[filters.dealType]?.[lang] : t("deal_type");
  const ptypeLabel = filters.propertyType ? PROPERTY_TYPES[filters.propertyType]?.[lang] : t("f_propertyType");

  const pick = (key, v) => { setFilter(key, v); setOpenId(null); };

  return (
    <div data-testid="results-filter-bar" ref={barRef} className="sticky top-0 z-40 shrink-0 bg-white border-b border-slate-200 shadow-sm">
      {openId && <div data-testid="filter-backdrop" onClick={() => setOpenId(null)} className="sm:hidden fixed inset-0 bg-slate-900/40 z-40" />}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex flex-wrap items-center gap-2.5">
          <Link to="/" data-testid="filter-logo" aria-hidden={!scrolled} tabIndex={scrolled ? 0 : -1} className="flex items-center shrink-0 mr-1">
            <img src={BRAND.logo} alt="Realty" className={`h-8 sm:h-16 w-auto transition-opacity duration-200 opacity-100 ${scrolled ? "sm:opacity-100" : "sm:opacity-0"}`} />
          </Link>
          <div className="sm:hidden ml-auto flex items-center gap-2">
            <button
              data-testid="mobile-filters-toggle"
              onClick={() => setShowFilters((s) => !s)}
              className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700"
            >
              <SlidersHorizontal className="h-3.5 w-3.5" /> {t("filters")}
              {activeCount > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-[#7C3AED] text-white text-[10px] font-bold">{activeCount}</span>
              )}
            </button>
            <ViewToggle view={view} setView={setView} t={t} compact testid="view-map-toggle-mobile" />
            <SortSelect sort={sort} setSort={setSort} t={t} testid="sort-dropdown-mobile" compact />
          </div>
          <div className={`${showFilters ? "flex" : "hidden"} sm:flex w-full sm:w-auto flex-wrap items-center gap-2.5`}>
          <Dropdown id="deal" label={dealLabel} active={!!filters.dealType} openId={openId} setOpenId={setOpenId} t={t} noOk>
            <OptionList options={dealOptions} value={filters.dealType} onSelect={(v) => pick("dealType", v)} testid="deal-options" />
          </Dropdown>

          <Dropdown id="ptype" label={ptypeLabel} active={!!filters.propertyType} openId={openId} setOpenId={setOpenId} t={t} noOk>
            <OptionList options={ptypeOptions} value={filters.propertyType} onSelect={(v) => pick("propertyType", v)} testid="ptype-options" />
          </Dropdown>

          <Dropdown id="location" label={t("cat_location")} active={anySet(filters, LOC_KEYS)} openId={openId} setOpenId={setOpenId} t={t}>
            <FieldSelect label={t("f_city")} value={filters.city} onChange={(v) => setFilter("city", v)} options={CITIES.map((c) => ({ v: c, l: c }))} placeholder={t("anyCity")} testid="loc-city" />
            <FieldInput label={t("f_district")} value={filters.district} onChange={(v) => setFilter("district", v)} testid="loc-district" />
            <FieldInput label={t("f_metro")} value={filters.metro} onChange={(v) => setFilter("metro", v)} testid="loc-metro" />
            <FieldSelect label={t("f_metro_distance")} value={filters.metroDist} onChange={(v) => setFilter("metroDist", v)} placeholder={t("any_short")} testid="loc-metro-dist"
              options={[{ v: "300", l: "≤ 300 м" }, { v: "500", l: "≤ 500 м" }, { v: "1000", l: "≤ 1000 м" }]} />
          </Dropdown>

          <Dropdown id="rooms" label={t("cat_rooms")} active={anySet(filters, ROOM_KEYS)} openId={openId} setOpenId={setOpenId} t={t}>
            <FieldSelect label={t("f_rooms")} value={filters.rooms} onChange={(v) => setFilter("rooms", v)} placeholder={t("anyRooms")} testid="rooms-qty"
              options={[{ v: "0", l: t("studio") }, { v: "1", l: "1+" }, { v: "2", l: "2+" }, { v: "3", l: "3+" }, { v: "4", l: "4+" }]} />
            <FieldSelect label={t("f_bedrooms")} value={filters.bedrooms} onChange={(v) => setFilter("bedrooms", v)} placeholder={t("anyRooms")} testid="rooms-bedrooms"
              options={[{ v: "1", l: "1+" }, { v: "2", l: "2+" }, { v: "3", l: "3+" }]} />
            <CheckRow label={t("f_wc_separate")} checked={filters.wcSeparate === "1"} onChange={(c) => setFilter("wcSeparate", c ? "1" : "")} testid="rooms-wc" />
            <CheckRow label={t("f_bath_tub")} checked={filters.bathTub === "1"} onChange={(c) => setFilter("bathTub", c ? "1" : "")} testid="rooms-tub" />
          </Dropdown>

          <Dropdown id="surface" label={t("cat_surface")} active={anySet(filters, SURF_KEYS)} openId={openId} setOpenId={setOpenId} t={t}>
            <RangeRow label={t("s_total")} minV={filters.areaMin} maxV={filters.areaMax} onMin={(v) => setFilter("areaMin", v)} onMax={(v) => setFilter("areaMax", v)} t={t} testid="surf-total" />
            <RangeRow label={t("s_kitchen")} minV={filters.kitchenMin} maxV={filters.kitchenMax} onMin={(v) => setFilter("kitchenMin", v)} onMax={(v) => setFilter("kitchenMax", v)} t={t} testid="surf-kitchen" />
            <RangeRow label={t("s_living")} minV={filters.livingMin} maxV={filters.livingMax} onMin={(v) => setFilter("livingMin", v)} onMax={(v) => setFilter("livingMax", v)} t={t} testid="surf-living" />
          </Dropdown>

          <Dropdown id="price" label={t("cat_price")} active={anySet(filters, PRICE_KEYS)} openId={openId} setOpenId={setOpenId} t={t}>
            <RangeRow label={t("f_price")} minV={filters.priceMin} maxV={filters.priceMax} onMin={(v) => setFilter("priceMin", v)} onMax={(v) => setFilter("priceMax", v)} t={t} testid="price" />
            <CheckRow label={t("p_no_commission")} checked={filters.noCommission === "1"} onChange={(c) => setFilter("noCommission", c ? "1" : "")} testid="price-no-commission" />
          </Dropdown>

          <Dropdown id="amenities" label={t("cat_amenities")} active={selectedAmenities.length > 0} openId={openId} setOpenId={setOpenId} t={t} wide alignRight>
            {AMENITIES.map((a) => (
              <div key={a.key} className={a.children ? "sm:col-span-2" : ""}>
                <CheckRow label={a[lang]} checked={selectedAmenities.includes(a.key)} onChange={() => toggleAmenity(a.key)} testid={`amenity-${a.key}`} />
                {a.children && (
                  <div className="ml-6 mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5">
                    {a.children.map((c) => (
                      <CheckRow key={c.key} label={c[lang]} checked={selectedAmenities.includes(c.key)} onChange={() => toggleAmenity(c.key)} testid={`amenity-${c.key}`} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </Dropdown>

          <Dropdown id="other" label={t("cat_other")} active={anySet(filters, OTHER_KEYS)} openId={openId} setOpenId={setOpenId} t={t} wide alignRight>
            <CheckRow label={t("o_parking")} checked={filters.parking === "1"} onChange={(c) => setFilter("parking", c ? "1" : "")} testid="other-parking" />
            <CheckRow label={t("o_underground_only")} checked={filters.undergroundOnly === "1"} onChange={(c) => setFilter("undergroundOnly", c ? "1" : "")} testid="other-underground" />
            <TriState label={t("o_furnished")} value={filters.furnished} onChange={(v) => setFilter("furnished", v)} t={t} testid="other-furnished" />
            <TriState label={t("o_pets")} value={filters.pets} onChange={(v) => setFilter("pets", v)} t={t} testid="other-pets" />
            <FieldDate label={t("o_available_from")} value={filters.availableFrom} onChange={(v) => setFilter("availableFrom", v)} testid="other-available" />
            <RangeRow label={t("o_built_year")} minV={filters.builtFrom} maxV={filters.builtTo} onMin={(v) => setFilter("builtFrom", v)} onMax={(v) => setFilter("builtTo", v)} t={t} testid="other-built" />
            <FieldSelect label={t("o_publication")} value={filters.publication} onChange={(v) => setFilter("publication", v)} placeholder={t("pub_any")} testid="other-publication"
              options={[{ v: "3d", l: t("pub_3d") }, { v: "week", l: t("pub_week") }, { v: "month", l: t("pub_month") }, { v: "older", l: t("pub_older") }]} />
            <CheckRow label={t("o_cargo_lift")} checked={filters.cargoLift === "1"} onChange={(c) => setFilter("cargoLift", c ? "1" : "")} testid="other-cargo" />
            <CheckRow label={t("o_direct_owner")} checked={filters.directOwner === "1"} onChange={(c) => setFilter("directOwner", c ? "1" : "")} testid="other-direct-owner" />
            <CheckRow label={t("o_kids_allowed")} checked={filters.kidsAllowed === "1"} onChange={(c) => setFilter("kidsAllowed", c ? "1" : "")} testid="other-kids-allowed" />
          </Dropdown>

          {/* mobile: Gold + Owner live inside the collapsed filters group */}
          <div className="sm:hidden w-full flex items-center gap-2.5 flex-wrap pt-1">
            <GoldOwnerToggles goldOn={goldOn} filters={filters} setFilter={setFilter} t={t} suffix="-mobile" />
          </div>
          </div>
        </div>

        {/* count line + (desktop) centered Gold/Owner toggles + (desktop) sort */}
        <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-y-2 gap-x-4">
          <div className="flex items-center gap-4 shrink-0">
            <p data-testid="results-count" className="text-sm text-slate-500 whitespace-nowrap">
              <span className="font-bold text-slate-900">{t("results_found")} {total}</span> {t("objects")}
            </p>
            {hasAny && (
              <button data-testid="reset-filters-button" onClick={resetFilters} className="flex items-center gap-1 text-sm font-semibold text-[#7C3AED] hover:text-[#6D28D9] shrink-0">
                <X className="h-3.5 w-3.5" /> {t("reset")}
              </button>
            )}
          </div>

          <div className="hidden sm:flex flex-1 items-center justify-center gap-2.5 flex-wrap">
            <GoldOwnerToggles goldOn={goldOn} filters={filters} setFilter={setFilter} t={t} />
          </div>

          <div className="hidden sm:flex items-center gap-2">
            <ViewToggle view={view} setView={setView} t={t} testid="view-map-toggle" />
            <SortSelect sort={sort} setSort={setSort} t={t} testid="sort-dropdown" />
          </div>
        </div>
      </div>
    </div>
  );
};

const GoldOwnerToggles = ({ goldOn, filters, setFilter, t, suffix = "" }) => (
  <>
    <button
      data-testid={`only-gold-toggle${suffix}`}
      onClick={() => setFilter("onlyGold", goldOn ? "0" : "")}
      className={`flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-bold whitespace-nowrap shrink-0 transition-all ${
        goldOn ? "border-amber-400 bg-gradient-to-r from-amber-500 to-[#F26B2A] text-white shadow-sm" : "border-slate-200 bg-white text-slate-500"
      }`}
    >
      <Crown className="h-3.5 w-3.5" /> {t("onlyGold")}
    </button>
    <button
      data-testid={`only-verified-toggle${suffix}`}
      onClick={() => setFilter("onlyVerified", filters.onlyVerified === "1" ? "" : "1")}
      className={`flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-bold whitespace-nowrap shrink-0 transition-all ${
        filters.onlyVerified === "1" ? "border-[#7C3AED] bg-[#7C3AED] text-white shadow-sm" : "border-slate-200 bg-white text-slate-500"
      }`}
    >
      <ShieldCheck className="h-3.5 w-3.5" /> {t("onlyVerified")}
    </button>
    <button
      data-testid={`owner-toggle${suffix}`}
      onClick={() => setFilter("directOwner", filters.directOwner === "1" ? "" : "1")}
      className={`flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-bold whitespace-nowrap shrink-0 transition-all ${
        filters.directOwner === "1" ? "border-[#7C3AED] bg-[#7C3AED] text-white shadow-sm" : "border-slate-200 bg-white text-slate-500"
      }`}
    >
      <User className="h-3.5 w-3.5" /> {t("owner_toggle")}
    </button>
  </>
);

const ViewToggle = ({ view, setView, t, compact, testid }) => {
  const mapOn = view === "map";
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => setView(mapOn ? "" : "map")}
      className={`flex items-center gap-1.5 rounded-full border font-bold whitespace-nowrap shrink-0 transition-all ${
        compact ? "px-3 py-1.5 text-xs" : "px-3.5 py-2 text-sm"
      } ${mapOn ? "border-[#7C3AED] bg-[#7C3AED] text-white shadow-sm" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}
    >
      {mapOn ? <LayoutGrid className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} /> : <Map className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />}
      {mapOn ? t("view_list") : t("view_map")}
    </button>
  );
};

const SortSelect = ({ sort, setSort, t, testid, compact }) => (
  <div className="relative shrink-0">
    <select data-testid={testid} value={sort} onChange={(e) => setSort(e.target.value)}
      className={`appearance-none truncate rounded-lg border border-slate-200 bg-white font-semibold text-slate-700 outline-none focus:border-[#F26B2A] cursor-pointer ${compact ? "pl-2.5 pr-7 py-1.5 text-xs max-w-[130px]" : "pl-3 pr-8 py-2 text-sm"}`}>
      <option value="gold">{t("sort_gold")}</option>
      <option value="price_asc">{t("sort_price_asc")}</option>
      <option value="price_desc">{t("sort_price_desc")}</option>
      <option value="new">{t("sort_new")}</option>
      <option value="size">{t("sort_size")}</option>
      <option value="availability">{t("sort_availability")}</option>
    </select>
    <ChevronDown className={`pointer-events-none absolute top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 ${compact ? "right-2" : "right-2.5"}`} />
  </div>
);

const Dropdown = ({ id, label, active, openId, setOpenId, children, t, wide, noOk, alignRight }) => {
  const open = openId === id;
  return (
    <div className={`relative shrink-0 ${open ? "z-50" : ""}`}>
      <button
        data-testid={`filter-${id}`}
        onClick={() => setOpenId(open ? null : id)}
        className={`flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold whitespace-nowrap transition-all ${
          active ? "border-[#7C3AED] bg-[#F3E8FF] text-[#7C3AED]" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
        }`}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid={`filter-panel-${id}`} className={`absolute ${alignRight ? "left-0 sm:left-auto sm:right-0" : "left-0"} mt-2 rounded-2xl border border-slate-200 bg-white shadow-xl p-4 z-50 max-sm:fixed max-sm:left-3 max-sm:right-3 max-sm:w-auto max-sm:max-h-[70vh] max-sm:overflow-y-auto ${wide ? "w-[min(90vw,560px)]" : "w-[min(90vw,300px)]"}`}>
          <div className={wide ? "grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3" : "space-y-3"}>{children}</div>
          {!noOk && (
            <div className="mt-4 flex justify-end">
              <button data-testid={`filter-ok-${id}`} onClick={() => setOpenId(null)}
                className="rounded-lg bg-gradient-to-r from-[#F26B2A] to-[#F59B42] text-white text-sm font-bold px-6 py-2">
                {t("ok")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const OptionList = ({ options, value, onSelect, testid }) => (
  <div data-testid={testid} className="space-y-1">
    {options.map((o) => (
      <button key={o.v || "any"} data-testid={`${testid}-${o.v || "any"}`} onClick={() => onSelect(o.v)}
        className={`w-full text-left rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
          (value || "") === o.v ? "bg-[#F3E8FF] text-[#7C3AED] font-bold" : "text-slate-700 hover:bg-slate-50"
        }`}>
        {o.l}
      </button>
    ))}
  </div>
);

const Label = ({ children }) => <span className="block text-xs font-semibold text-slate-500 mb-1">{children}</span>;

const FieldSelect = ({ label, value, onChange, options, placeholder, testid }) => (
  <div>
    <Label>{label}</Label>
    <div className="relative">
      <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none rounded-lg border border-slate-200 bg-white px-3 py-2 pr-8 text-sm font-medium text-slate-800 outline-none focus:border-[#F26B2A]">
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
    </div>
  </div>
);

const FieldInput = ({ label, value, onChange, testid }) => (
  <div>
    <Label>{label}</Label>
    <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 outline-none focus:border-[#F26B2A]" />
  </div>
);

const FieldDate = ({ label, value, onChange, testid }) => (
  <div>
    <Label>{label}</Label>
    <input type="date" data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 outline-none focus:border-[#F26B2A]" />
  </div>
);

const RangeRow = ({ label, minV, maxV, onMin, onMax, t, testid }) => (
  <div>
    <Label>{label}</Label>
    <div className="flex items-center gap-2">
      <input type="number" data-testid={`${testid}-min`} value={minV || ""} onChange={(e) => onMin(e.target.value)} placeholder={t("from_short")}
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 outline-none focus:border-[#F26B2A]" />
      <span className="text-slate-300">–</span>
      <input type="number" data-testid={`${testid}-max`} value={maxV || ""} onChange={(e) => onMax(e.target.value)} placeholder={t("to_short")}
        className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 outline-none focus:border-[#F26B2A]" />
    </div>
  </div>
);

const CheckRow = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2.5 cursor-pointer select-none py-0.5">
    <input type="checkbox" data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)}
      className="h-4 w-4 rounded border-slate-300 text-[#7C3AED] accent-[#7C3AED]" />
    <span className="text-sm font-medium text-slate-700">{label}</span>
  </label>
);

const TriState = ({ label, value, onChange, t, testid }) => (
  <div>
    <Label>{label}</Label>
    <div data-testid={testid} className="inline-flex rounded-lg bg-slate-100 p-0.5">
      {[{ v: "", l: t("any_short") }, { v: "yes", l: t("yes") }, { v: "no", l: t("no") }].map((o) => (
        <button key={o.v || "any"} data-testid={`${testid}-${o.v || "any"}`} onClick={() => onChange(o.v)}
          className={`px-3 py-1 rounded-md text-xs font-bold transition-all ${(value || "") === o.v ? "bg-white text-[#7C3AED] shadow-sm" : "text-slate-500"}`}>
          {o.l}
        </button>
      ))}
    </div>
  </div>
);
